import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { searchPapers, fetchCategories } from '../utils/api';
import '../styles/PageStyles.css';

const Search = () => {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [categories, setCategories] = useState([]);
  
  // Filter states
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [authorFilter, setAuthorFilter] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const resultsPerPage = 10;

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await fetchCategories();
        // Ensure categories is always an array
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      } catch (err) {
        console.error('Error loading categories:', err);
      }
    };
    
    loadCategories();
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!query.trim() && !categoryFilter && !dateFilter && !authorFilter) {
      return;
    }
    
    try {
      setLoading(true);
      setSearchPerformed(true);
      setCurrentPage(1);
      
      const results = await searchPapers({
        query,
        category: categoryFilter,
        date: dateFilter,
        author: authorFilter
      });
      
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching papers:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setQuery('');
    setCategoryFilter('');
    setDateFilter('');
    setAuthorFilter('');
    setSearchPerformed(false);
    setSearchResults([]);
  };

  // Get current results for pagination
  const indexOfLastResult = currentPage * resultsPerPage;
  const indexOfFirstResult = indexOfLastResult - resultsPerPage;
  const currentResults = searchResults.slice(indexOfFirstResult, indexOfLastResult);
  
  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <>
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">Search Publications</h1>
          <p className="page-subtitle">
            Find research articles by title, keywords, authors, or browse by category
          </p>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="breadcrumb-nav">
        <div className="container">
          <nav className="breadcrumb">
            <span className="breadcrumb-item">
              <Link to="/">Home</Link>
            </span>
            <span className="breadcrumb-item">
              <i className="fas fa-chevron-right"></i>
            </span>
            <span className="breadcrumb-item active">Search</span>
          </nav>
        </div>
      </div>

      <div className="container py-4">
        <div className="row">
          <div className="col-lg-10 mx-auto">
            {/* Search Form */}
            <div className="publication-card mb-4">
              <form onSubmit={handleSearch}>
                <div className="input-group mb-3">
                  <span className="input-group-text bg-white">
                    <i className="fas fa-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    placeholder="Search by title, abstract, keywords..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  <button className="btn btn-primary px-4" type="submit">
                    Search
                  </button>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <label className="form-label small text-muted">Category</label>
                    <select
                      className="form-select"
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                    >
                      <option value="">All Categories</option>
                      {categories.map((category, index) => (
                        <option key={index} value={typeof category === 'object' ? category.name : category}>
                          {typeof category === 'object' ? category.name : category}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label small text-muted">Publication Date</label>
                    <select
                      className="form-select"
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                    >
                      <option value="">Any Date</option>
                      <option value="last_year">Last Year</option>
                      <option value="last_3_years">Last 3 Years</option>
                      <option value="last_5_years">Last 5 Years</option>
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label small text-muted">Author Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter author name..."
                      value={authorFilter}
                      onChange={(e) => setAuthorFilter(e.target.value)}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={clearFilters}
                  >
                    <i className="fas fa-times me-1"></i> Clear Filters
                  </button>

                  <button type="submit" className="btn btn-primary">
                    <i className="fas fa-filter me-1"></i> Apply Filters
                  </button>
                </div>
              </form>
            </div>
          
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Searching for papers...</p>
            </div>
          ) : searchPerformed ? (
            <>
              <div className="card">
                <div className="card-header bg-primary text-white">
                  <h3 className="mb-0">Search Results</h3>
                </div>
                <div className="card-body">
                  {searchResults.length > 0 ? (
                    <>
                      <p>Found {searchResults.length} results</p>
                      
                      {currentResults.map(paper => (
                        <div key={paper.id} className="publication-card mb-4">
                          <h4 className="publication-title">
                            <Link
                              to={`/paper/${paper.id}`}
                              state={{ from: '/search', fromLabel: 'Search Results' }}
                              className="text-decoration-none"
                            >
                              {paper.title}
                            </Link>
                          </h4>
                          <div className="publication-meta mb-2">
                            <span className="meta-item">
                              <i className="fas fa-users me-1"></i>
                              {paper.authors && Array.isArray(paper.authors) && paper.authors.length > 0
                                ? paper.authors.map(author => typeof author === 'object' ? author.name : author).join(', ')
                                : 'Unknown Authors'}
                            </span>
                          </div>
                          <p className="publication-abstract">
                            {paper.abstract && paper.abstract.length > 200
                              ? `${paper.abstract.substring(0, 200)}...`
                              : paper.abstract || 'No abstract available'}
                          </p>
                          <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div className="publication-keywords">
                              {paper.keywords && typeof paper.keywords === 'string'
                                ? paper.keywords.split(',').map((keyword, index) => (
                                    <span key={index} className="keyword-badge me-1 mb-1">
                                      {keyword.trim()}
                                    </span>
                                  ))
                                : null}
                            </div>
                            <small className="text-muted">
                              {paper.publishedDate
                                ? `Published: ${formatDate(paper.publishedDate)}`
                                : paper.submittedDate
                                  ? `Submitted: ${formatDate(paper.submittedDate)}`
                                  : paper.createdAt
                                    ? `Created: ${formatDate(paper.createdAt)}`
                                    : ''}
                            </small>
                          </div>
                          <div className="mt-3">
                            <Link
                              to={`/paper/${paper.id}`}
                              state={{ from: '/search', fromLabel: 'Search Results' }}
                              className="btn btn-sm btn-primary"
                            >
                              <i className="fas fa-eye me-1"></i> View Details
                            </Link>
                          </div>
                        </div>
                      ))}
                      
                      {/* Pagination */}
                      {searchResults.length > resultsPerPage && (
                        <nav>
                          <ul className="pagination justify-content-center">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                Previous
                              </button>
                            </li>
                            
                            {Array.from({ length: Math.ceil(searchResults.length / resultsPerPage) }, (_, i) => (
                              <li 
                                key={i + 1} 
                                className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}
                              >
                                <button 
                                  className="page-link"
                                  onClick={() => paginate(i + 1)}
                                >
                                  {i + 1}
                                </button>
                              </li>
                            ))}
                            
                            <li className={`page-item ${currentPage === Math.ceil(searchResults.length / resultsPerPage) ? 'disabled' : ''}`}>
                              <button 
                                className="page-link"
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === Math.ceil(searchResults.length / resultsPerPage)}
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-5">
                      <i className="fas fa-search fa-3x text-muted mb-3"></i>
                      <h4>No results found</h4>
                      <p>Try different keywords or remove some filters</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <div className="publication-card">
                <i className="fas fa-search fa-3x text-muted mb-3"></i>
                <h4>Start your search</h4>
                <p className="text-muted">Enter keywords, select filters, or browse by category to find research articles</p>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Search;
